import axios from "axios";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import HotelAgent from "./pages/HotelAgent";
import AgentsPage from "./pages/AgentsPage";
import Navbar from "./components/Header/Navbar";
import Footer from "./components/Header/Footer";
import ErrorPage from "./pages/ErrorPage";
import GuestsPage from "./dashboard/Guest/GuestPage";
import BookingsPage from "./dashboard/Bookings/BookingsPage";
import RoomsPage from "./dashboard/Rooms/RoomsPage";
import SettingsPage from "./dashboard/manager/SettingsPage";
import NotificationsPage from "./dashboard/manager/NotificationsPage";
import CheckInOutPage from "./dashboard/manager/CheckInOutPage";
import RevenuePage from "./dashboard/manager/RevenuePage";
import OccupancyPage from "./dashboard/manager/OccupancyPage";
import GuestAnalyticsPage from "./dashboard/manager/GuestAnalyticsPage";
import ReportsPage from "./dashboard/manager/ReportsPage";
import PaymentsPage from "./dashboard/manager/PaymentsPage";
import FrontDeskPage from "./dashboard/manager/FrontDeskPage";
import AdvancedAnalyticsPage from "./dashboard/manager/AdvancedAnalyticsPage";
import GuestProfilePage from "./dashboard/Guest/GuestProfilePage";
import UsersPage from "./dashboard/manager/UsersPage";
import ChannelAnalyticsPage from "./dashboard/manager/ChannelAnalyticsPage";
import PricingSuggestionsPage from "./dashboard/manager/PricingSuggestionsPage";
import PricingPage from "./dashboard/manager/PricingPage";
import PaymentRedirect from "./pages/PaymentRedirect";
import MessageMetricsPage from "./dashboard/manager/MessageMetricsPage";
import NightAuditPage from "./dashboard/manager/NightAuditPage";
import SettlementPage from "./dashboard/manager/SettlementPage";
import HousekeepingPage from "./dashboard/manager/HousekeepingPage";
import StaffAttendancePage from "./dashboard/manager/StaffAttendancePage";
import BookingCalendarPage from "./dashboard/manager/BookingCalendarPage";
import HousekeeperDashboard from "./dashboard/housekeeping/HousekeeperDashboard";
import HousekeepingManagerDashboard from "./dashboard/housekeeping/HousekeepingManagerDashboard";
import MinibarCatalog from "./dashboard/minibar/MinibarCatalog";
import MaintenancePage from "./dashboard/manager/MaintenancePage";
import GuestJourneyPage from "./dashboard/manager/GuestJourneyPage";
import ShiftHandoverPage from "./dashboard/manager/ShiftHandoverPage";
import PreCheckinPanel from "./dashboard/manager/PreCheckinPanel";
import MorningBriefingPage from "./dashboard/manager/MorningBriefingPage";
import ChatPage from "./dashboard/chat/ChatPage";
import ChatAnalyticsPage from "./dashboard/chat/ChatAnalyticsPage";
import BotsPage from "./pages/BotsPage";
import FeedbackPage from "./pages/FeedbackPage";
import PreCheckinPage from "./pages/PreCheckinPage";
import ReceiptPage from "./pages/ReceiptPage";
import RegistrationCardPage from "./dashboard/manager/RegistrationCardPage";

import Login from "./dashboard/login/Login";
import ManagerDashboard from "./dashboard/manager/ManagerDashboard";
import ReceptionDashboard from "./dashboard/reception/ReceptionDashboard";
import RoomServiceDashboard from "./dashboard/roomservice/RoomServiceDashboard";
import RoomServiceManagerDashboard from "./dashboard/roomservice/RoomServiceManagerDashboard";
import KitchenDisplay from "./dashboard/roomservice/KitchenDisplay";
import SpaDashboard from "./dashboard/spa/SpaDashboard";

import ProtectedRoute from "./auth/ProtectedRoute";

// Attach JWT to every request
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("ai2_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// On 401, clear stale session and go to login
axios.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      const onDashboard = window.location.pathname.startsWith("/dashboard") &&
                          !window.location.pathname.startsWith("/dashboard/login");
      if (onDashboard) {
        ["ai2_token", "ai2_role", "ai2_name", "ai2_hotel"].forEach(k => localStorage.removeItem(k));
        window.location.href = "/dashboard/login";
      }
    }
    return Promise.reject(err);
  }
);

// A wrapper so we can hide Navbar/Footer on dashboard pages
function LayoutWrapper() {
  const location = useLocation();

  const hideLayout = location.pathname === "/" || location.pathname === "/about" || location.pathname === "/agents" || location.pathname === "/bots" || location.pathname === "/hotel" || location.pathname.startsWith("/dashboard") || location.pathname === "/dashboard/roomservice/kitchen" || location.pathname === "/pre-checkin" || location.pathname === "/payment-redirect" || location.pathname.startsWith("/receipt/") || location.pathname.startsWith("/rc/") || location.pathname.startsWith("/feedback/");

  return (
    <>
      {!hideLayout && <Navbar />}
      
      <Routes>
        {/* Public pages */}
        <Route path="/" element={<HomePage />} />
        <Route path="/payment-redirect" element={<PaymentRedirect />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/agents" element={<AgentsPage />} />
        <Route path="/bots"   element={<BotsPage />} />
        <Route path="/hotel" element={<HotelAgent />} />
        <Route path="/demo" element={<Navigate to="/hotel" replace />} />
        <Route path="/contact" element={<Navigate to="/about" replace />} />
        <Route path="/privacy" element={<Navigate to="/about" replace />} />
        <Route path="/terms" element={<Navigate to="/about" replace />} />
        <Route path="*" element={<ErrorPage />} />

        {/* Dashboard Login */}
        <Route path="/dashboard/login" element={<Login />} />

        {/* Protected Dashboard Routes */}

        <Route
          path="/dashboard/manager"
          element={
            <ProtectedRoute allowedRoles={["manager","admin","frontdesk","reception"]}>
              <ManagerDashboard />
            </ProtectedRoute>
          }
        />

<Route path="/dashboard/manager/guests"         element={<ProtectedRoute allowedRoles={["manager","admin","frontdesk","reception","livechat"]}><GuestsPage /></ProtectedRoute>} />
<Route path="/dashboard/reception/guests"       element={<ProtectedRoute allowedRoles={["reception","frontdesk"]}><GuestsPage /></ProtectedRoute>} />
<Route path="/dashboard/manager/bookings"       element={<ProtectedRoute allowedRoles={["manager","admin","frontdesk","reception"]}><BookingsPage /></ProtectedRoute>} />
<Route path="/dashboard/manager/rooms"          element={<ProtectedRoute allowedRoles={["manager","admin","frontdesk","reception"]}><RoomsPage /></ProtectedRoute>} />
<Route path="/dashboard/manager/settings"       element={<ProtectedRoute allowedRoles={["manager","admin","reception","frontdesk"]}><SettingsPage /></ProtectedRoute>} />
<Route path="/dashboard/manager/notifications"  element={<ProtectedRoute allowedRoles={["manager","admin","frontdesk","reception"]}><NotificationsPage /></ProtectedRoute>} />
<Route path="/dashboard/manager/checkinout"     element={<ProtectedRoute allowedRoles={["manager","admin","reception","frontdesk"]}><CheckInOutPage /></ProtectedRoute>} />
<Route path="/dashboard/manager/revenue"        element={<ProtectedRoute allowedRoles={["manager","admin"]}><RevenuePage /></ProtectedRoute>} />
<Route path="/dashboard/manager/occupancy"      element={<ProtectedRoute allowedRoles={["manager","admin"]}><OccupancyPage /></ProtectedRoute>} />
<Route path="/dashboard/manager/guest-analytics" element={<ProtectedRoute allowedRoles={["manager","admin"]}><GuestAnalyticsPage /></ProtectedRoute>} />
<Route path="/dashboard/manager/reports"        element={<ProtectedRoute allowedRoles={["manager","admin"]}><ReportsPage /></ProtectedRoute>} />
<Route path="/dashboard/manager/payments"       element={<ProtectedRoute allowedRoles={["manager","admin","frontdesk","reception"]}><PaymentsPage /></ProtectedRoute>} />
<Route path="/dashboard/manager/frontdesk"      element={<ProtectedRoute allowedRoles={["manager","admin","reception","frontdesk"]}><FrontDeskPage /></ProtectedRoute>} />
<Route path="/dashboard/manager/analytics"      element={<ProtectedRoute allowedRoles={["manager","admin"]}><AdvancedAnalyticsPage /></ProtectedRoute>} />
<Route path="/dashboard/manager/users"            element={<ProtectedRoute allowedRoles={["manager","admin"]}><UsersPage /></ProtectedRoute>} />
<Route path="/dashboard/manager/guests/:phone"   element={<ProtectedRoute allowedRoles={["manager","admin","reception","frontdesk","livechat"]}><GuestProfilePage /></ProtectedRoute>} />
<Route path="/dashboard/manager/channel-analytics" element={<ProtectedRoute allowedRoles={["manager","admin"]}><ChannelAnalyticsPage /></ProtectedRoute>} />
<Route path="/dashboard/manager/pricing"         element={<ProtectedRoute allowedRoles={["manager","admin"]}><PricingSuggestionsPage /></ProtectedRoute>} />
<Route path="/dashboard/manager/pricing-config"  element={<ProtectedRoute allowedRoles={["manager","admin","reception","frontdesk"]}><PricingPage /></ProtectedRoute>} />
<Route path="/dashboard/manager/message-metrics" element={<ProtectedRoute allowedRoles={["manager","admin"]}><MessageMetricsPage /></ProtectedRoute>} />
<Route path="/dashboard/manager/night-audit"     element={<ProtectedRoute allowedRoles={["manager","admin","reception","frontdesk"]}><NightAuditPage /></ProtectedRoute>} />
<Route path="/dashboard/manager/settlement"      element={<ProtectedRoute allowedRoles={["manager","admin","reception","frontdesk"]}><SettlementPage /></ProtectedRoute>} />
<Route path="/dashboard/manager/housekeeping"    element={<ProtectedRoute allowedRoles={["manager","admin","reception","frontdesk"]}><HousekeepingPage /></ProtectedRoute>} />
<Route path="/dashboard/manager/staff-attendance" element={<ProtectedRoute allowedRoles={["manager","admin","room_service_manager","spa_manager","housekeeper_manager"]}><StaffAttendancePage /></ProtectedRoute>} />
<Route path="/dashboard/manager/minibar"          element={<ProtectedRoute allowedRoles={["manager","admin"]}><MinibarCatalog /></ProtectedRoute>} />
<Route path="/dashboard/manager/booking-calendar" element={<ProtectedRoute allowedRoles={["manager","admin","reception","frontdesk"]}><BookingCalendarPage /></ProtectedRoute>} />
<Route path="/dashboard/manager/maintenance"    element={<ProtectedRoute allowedRoles={["manager","admin","frontdesk","reception"]}><MaintenancePage /></ProtectedRoute>} />
<Route path="/dashboard/manager/guest-journey"  element={<ProtectedRoute allowedRoles={["manager","admin","frontdesk","reception"]}><GuestJourneyPage /></ProtectedRoute>} />
<Route path="/dashboard/manager/shift-handover" element={<ProtectedRoute allowedRoles={["manager","admin","frontdesk","reception"]}><ShiftHandoverPage /></ProtectedRoute>} />
<Route path="/dashboard/manager/pre-checkin"    element={<ProtectedRoute allowedRoles={["manager","admin","frontdesk","reception"]}><PreCheckinPanel /></ProtectedRoute>} />
<Route path="/dashboard/manager/morning-briefing" element={<ProtectedRoute allowedRoles={["manager","admin","frontdesk","reception"]}><MorningBriefingPage /></ProtectedRoute>} />
<Route path="/dashboard/manager/chat"            element={<ProtectedRoute allowedRoles={["manager","admin","frontdesk","reception","livechat"]}><ChatPage /></ProtectedRoute>} />
<Route path="/dashboard/manager/chat-analytics" element={<ProtectedRoute allowedRoles={["manager","admin"]}><ChatAnalyticsPage /></ProtectedRoute>} />
<Route path="/pre-checkin" element={<PreCheckinPage />} />
        <Route path="/feedback/:token" element={<FeedbackPage />} />
        <Route path="/receipt/:token" element={<ReceiptPage />} />
        <Route path="/rc/:bookingId" element={<RegistrationCardPage />} />
<Route path="/dashboard/reception/guests/:phone" element={<ProtectedRoute allowedRoles={["reception","frontdesk"]}><GuestProfilePage /></ProtectedRoute>} />
<Route
  path="/dashboard/reception/bookings"
  element={
    <ProtectedRoute allowedRoles={["reception","frontdesk"]}>
      <BookingsPage />
    </ProtectedRoute>
  }
/>
        <Route
          path="/dashboard/reception"
          element={
            <ProtectedRoute allowedRoles={["reception","frontdesk"]}>
              <ReceptionDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/roomservice"
          element={
            <ProtectedRoute allowedRoles={["room_service"]}>
              <RoomServiceDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/roomservice/manager"
          element={
            <ProtectedRoute allowedRoles={["room_service_manager","manager","admin"]}>
              <RoomServiceManagerDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/roomservice/kitchen"
          element={
            <ProtectedRoute allowedRoles={["room_service","room_service_manager","manager","admin"]}>
              <KitchenDisplay />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/spa"
          element={
            <ProtectedRoute allowedRoles={["spa", "spa_manager"]}>
              <SpaDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/housekeeping"
          element={
            <ProtectedRoute allowedRoles={["housekeeper", "manager", "admin"]}>
              <HousekeeperDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/housekeeping/manager"
          element={
            <ProtectedRoute allowedRoles={["housekeeper_manager", "manager", "admin"]}>
              <HousekeepingManagerDashboard />
            </ProtectedRoute>
          }
        />
      </Routes>

      {!hideLayout && <Footer />}
    </>
  );
}

export default function App() {
  return (
    <Router>
      <LayoutWrapper />
    </Router>
  );
}
